import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(() => localStorage.getItem("sw_token"));
    const [loading, setLoading] = useState(true);

    const authHeaders = useCallback(
        () => (token ? { Authorization: `Bearer ${token}` } : {}),
        [token]
    );

    useEffect(() => {
        // If returning from OAuth callback, let AuthCallback handle session exchange first
        if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
            setLoading(false);
            return;
        }
        const boot = async () => {
            if (!token) { setLoading(false); return; }
            try {
                const res = await axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` } });
                setUser(res.data);
            } catch (e) {
                localStorage.removeItem("sw_token");
                setToken(null);
            } finally {
                setLoading(false);
            }
        };
        boot();
    }, [token]);

    const login = async (email, password) => {
        const res = await axios.post(`${API}/auth/login`, { email, password });
        localStorage.setItem("sw_token", res.data.access_token);
        setToken(res.data.access_token);
        setUser(res.data.user);
        return res.data.user;
    };

    const signup = async (name, email, password) => {
        const res = await axios.post(`${API}/auth/signup`, { name, email, password });
        localStorage.setItem("sw_token", res.data.access_token);
        setToken(res.data.access_token);
        setUser(res.data.user);
        return res.data.user;
    };

    const setSessionFromOAuth = (accessToken, userData) => {
        localStorage.setItem("sw_token", accessToken);
        setToken(accessToken);
        setUser(userData);
    };

    const signupStart = async (name, email, password) => {
        const res = await axios.post(`${API}/auth/signup-start`, { name, email, password });
        return res.data;
    };

    const signupVerify = async (pending_id, code) => {
        const res = await axios.post(`${API}/auth/signup-verify`, { pending_id, code });
        localStorage.setItem("sw_token", res.data.access_token);
        setToken(res.data.access_token);
        setUser(res.data.user);
        return res.data.user;
    };

    const signupResend = async (pending_id) => {
        const res = await axios.post(`${API}/auth/signup-resend`, { pending_id });
        return res.data;
    };

    const logout = () => {
        localStorage.removeItem("sw_token");
        setToken(null);
        setUser(null);
    };

    return (
        <AuthContext.Provider value={{ user, token, loading, login, signup, signupStart, signupVerify, signupResend, setSessionFromOAuth, logout, authHeaders }}>
            {children}
        </AuthContext.Provider>
    );
};

export const useAuth = () => useContext(AuthContext);
